import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";

import { formatMinutes } from "sj-ui-toolkit/src/utils/time";
import { C } from "sj-ui-toolkit/src/atlas/utils";

import S from "./PathDetails.module.scss";

export const PathDetails = ({
  title,
  description,
  isEnrolled,
  status,
  pointsEarned,
  totalPoints,
  lessonsCompleted,
  lessonsInPath,
  estimatedTimeRemaining,
  enrollInPath,
  unEnrollInPath,
  pathType,
  currentLevelEnrollment,
  hasOutstandingStrictAssignment,
  isInactivePath,
  isLevelLocked,
  disabledEnrollButton,
}) => {
  const handleEnrollClick = event => {
    if (disabledEnrollButton) return;

    if (isEnrolled) {
      unEnrollInPath();
    } else if (currentLevelEnrollment) {
      confirmPopup({
        target: event.currentTarget,
        message: `Enrolling in ${title} will pause your enrollment in ${currentLevelEnrollment.name} in the ${currentLevelEnrollment.levelName} Level. Would you like to continue?`,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes",
        rejectLabel: "No",
        accept: () => enrollInPath(true),
        style: { maxWidth: "400px" },
      });
    } else {
      enrollInPath();
    }
  };

  // Determine tooltip message based on priority order -- hasOutstandingStrictAssignment > isInactivePath > isLevelLocked
  const getTooltipMessage = () => {
    if (hasOutstandingStrictAssignment) {
      return "You are enrolled in a strict Assignment and have to complete your Assignment first before you can enroll in this Path.";
    }
    if (isInactivePath) {
      return "This Path is inactive for your organization. Please ask your admin to make it active so you can enroll and receive a certificate of completion.";
    }
    if (isLevelLocked) {
      return "This Path will become unlocked after you complete a Path in the previous levels.";
    }
    if (status === "passed") {
      return "Congratulations, you've already completed this path! Feel free to revisit any lessons if you'd like.";
    }
    return null;
  };

  const tooltipMessage = getTooltipMessage();
  const showTooltip = disabledEnrollButton && tooltipMessage;

  return (
    <div className={C(S.pathDetailsContainer, "pb-8")}>
      <ConfirmPopup />
      <div className={S.pathDetailsHeader}>
        <div className={S.pointsContainer}>
          <Tag value={pathType} severity="secondary" rounded={true} />
          {totalPoints > 0 && (
            <span className={C(S.mutedText, "atlas-h5")} aria-label={`${totalPoints} total points`}>
              {" "}
              +{totalPoints}
            </span>
          )}
        </div>
        {isEnrolled !== "loading" && (
          <>
            {showTooltip && (
              <Tooltip
                target=".enroll-button-wrapper"
                content={tooltipMessage}
                position="right"
                pt={{ root: { style: { maxWidth: "400px" } } }}
              />
            )}
            <span
              className={showTooltip ? "enroll-button-wrapper" : ""}
              style={{ display: "inline-block" }}
            >
              <Button
                label={isEnrolled ? `Unenroll from ${pathType}` : `Enroll in ${pathType}`}
                severity={isEnrolled && "secondary"}
                onClick={handleEnrollClick}
                disabled={disabledEnrollButton}
                aria-disabled={disabledEnrollButton || undefined}
                aria-describedby={showTooltip ? "enroll-button-tooltip" : undefined}
              />
            </span>
            {showTooltip && (
              <span id="enroll-button-tooltip" className="sr-only">
                {tooltipMessage}
              </span>
            )}
          </>
        )}
      </div>

      <h1 className={C("atlas-h2-alt", "leading-tight")}>{title}</h1>
      <p className="body-lg">{description}</p>

      <div className={S.pathProgressContainer} role="region" aria-label="Path progress">
        <div className={S.section} role="group" aria-labelledby="stat-points-earned">
          <h2 className={C(S.mutedText, "atlas-h5")} id="stat-points-earned">
            Points Earned
          </h2>
          <p className="atlas-h2">{pointsEarned}</p>
          <p className={S.mutedText}>/ {totalPoints} available</p>
        </div>

        <div className={S.section} role="group" aria-labelledby="stat-lessons-completed">
          <h2 className={C(S.mutedText, "atlas-h5")} id="stat-lessons-completed">
            Lessons Completed
          </h2>
          <p className="atlas-h2">{lessonsCompleted}</p>
          <p className={S.mutedText}>/ {lessonsInPath} total lessons</p>
        </div>

        <div className={S.section} role="group" aria-labelledby="stat-time-remaining">
          <h2 className={C(S.mutedText, "atlas-h5")} id="stat-time-remaining">
            Est Time Remaining
          </h2>
          <p className="atlas-h2">{formatMinutes(estimatedTimeRemaining)}</p>
        </div>
      </div>
    </div>
  );
};
